import { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import type { ApolloQueryResult } from '@apollo/client';
import {
  useSaveFormDraftMutation,
  SaveFormDraftInput,
  ProviderProgramAssessmentQuery,
  ProviderProgramAssessmentQueryVariables,
} from '../../generated/graphql';
import { CompleteProviderFormData } from '../pages/CompleteProviderForm/helpers';
import { isPatientContactAssessmentQuestion } from '../lib/assessments';
import {
  PATIENT_CONTACT_NEW_SENTINEL,
  FormQuestion,
} from '../lib/program-assessment';
import { formValuesToDraftInput } from '../lib/formDraftHelpers';
import { logger } from '../../lib/logger';
import {
  FORM_AUTO_SAVE_DEBOUNCE_MS,
  FORM_DRAFT_REFETCH_DELAY_MS,
} from '../lib/constants';
import { coerceCheckboxBoolean } from '../lib/register-options';

interface UseFormAutoSaveOptions {
  programAssessmentId: string;
  formData: CompleteProviderFormData;
  questions: FormQuestion[];
  debounceMs?: number;
  enabled?: boolean;
  refetchQuery?: (
    variables?: Partial<ProviderProgramAssessmentQueryVariables>,
    options?: {
      fetchPolicy?:
        | 'network-only'
        | 'cache-first'
        | 'cache-and-network'
        | 'no-cache';
    },
  ) => Promise<ApolloQueryResult<ProviderProgramAssessmentQuery>>;
  // Multi-form mode support
  programAssessmentIds?: string[]; // Array of all assessment IDs in multi-form mode
  questionsMap?: Map<string, FormQuestion[]>; // Map of assessment ID to questions in multi-form mode
}

interface UseFormAutoSaveReturn {
  isSaving: boolean;
  lastSaved: Date | null;
  saveError: Error | null;
  manualSave: () => Promise<void>;
}

/**
 * Hook for automatically saving form drafts as the user types.
 *
 * Features:
 * - Debounced auto-save to avoid excessive API calls
 * - Change detection to only save when form data actually changes
 * - Support for both single form and multi-form modes
 * - Signature field saving (for single form mode)
 * - Automatic cache refetch after save
 *
 * @param options - Configuration options for auto-save
 * @param options.programAssessmentId - The ID of the program assessment being edited
 * @param options.formData - The current form data from react-hook-form
 * @param options.questions - The assessment questions for mapping answers
 * @param options.debounceMs - Debounce delay in milliseconds (default: 2000)
 * @param options.enabled - Whether auto-save is enabled (default: true)
 * @param options.refetchQuery - Optional Apollo refetch function to update cache after save
 * @returns Auto-save state and manual save function
 *
 * @example
 * ```tsx
 * const { isSaving, lastSaved, saveError, manualSave } = useFormAutoSave({
 *   programAssessmentId: '123',
 *   formData: formValues,
 *   questions: assessmentQuestions,
 *   enabled: !formIsReadOnly,
 *   refetchQuery: refetchProgramAssessment,
 * });
 * ```
 */
export const useFormAutoSave = ({
  programAssessmentId,
  formData,
  questions,
  debounceMs = FORM_AUTO_SAVE_DEBOUNCE_MS,
  enabled = true,
  refetchQuery,
  programAssessmentIds,
  questionsMap,
}: UseFormAutoSaveOptions): UseFormAutoSaveReturn => {
  const isMultiFormMode = Boolean(
    programAssessmentIds && questionsMap && programAssessmentIds.length > 0,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<Error | null>(null);

  const [saveFormDraft] = useSaveFormDraftMutation();

  // Ref to track if it's the initial render to avoid saving on mount
  const isFirstRender = useRef(true);
  // Ref to store the previous programAssessmentId to detect changes
  const previousProgramAssessmentId = useRef(programAssessmentId);
  // Ref to store the latest form data to avoid stale closures in debounce
  const latestFormData = useRef(formData);
  // Ref to store the previous normalized form data string for efficient comparison
  const previousNormalizedFormData = useRef<string | null>(null);

  useEffect(() => {
    latestFormData.current = formData;
  }, [formData]);

  /**
   * Normalizes a single answer value for comparison.
   * Handles both single selection (value) and multi-select (values).
   */
  const normalizeAnswer = useCallback(
    (answer: {
      value?: string;
      values?: string[];
      contactId?: string;
      newContactDraft?: {
        firstName?: string;
        lastName?: string;
        email?: string;
        phoneNumber?: string;
        relationshipDescription?: string;
        addressStreet?: string;
        addressCity?: string;
        addressState?: string;
        addressPostalCode?: string;
        addressCountry?: string;
      };
    }): {
      value: string | null;
      values: string[];
      contactId: string | null;
      newContactDraft: string | null;
    } => {
      const normalized: {
        value: string | null;
        values: string[];
        contactId: string | null;
        newContactDraft: string | null;
      } = {
        value: null,
        values: [],
        contactId: null,
        newContactDraft: null,
      };

      // Single selection value (for radio buttons, single choice, text inputs, etc.)
      const value = answer?.value;
      if (value !== undefined && value !== null) {
        // Convert to string and trim, but preserve empty strings as empty strings
        const strValue = String(value).trim();
        normalized.value = strValue === '' ? null : strValue; // Normalize empty strings to null
      }

      // Multi-select values (for checkboxes)
      if (
        answer?.values &&
        Array.isArray(answer.values) &&
        answer.values.length > 0
      ) {
        normalized.values = answer.values.slice().sort(); // Sort for consistent comparison
      }

      const contactId = answer?.contactId;
      if (contactId !== undefined && contactId !== null) {
        const c = String(contactId).trim();
        if (c !== '' && c !== PATIENT_CONTACT_NEW_SENTINEL) {
          normalized.contactId = c;
        }
      }

      const d = answer?.newContactDraft;
      if (d) {
        const draftObj = {
          firstName: (d.firstName ?? '').trim(),
          lastName: (d.lastName ?? '').trim(),
          email: (d.email ?? '').trim(),
          phoneNumber: (d.phoneNumber ?? '').trim(),
          relationshipDescription: (d.relationshipDescription ?? '').trim(),
          addressStreet: (d.addressStreet ?? '').trim(),
          addressCity: (d.addressCity ?? '').trim(),
          addressState: (d.addressState ?? '').trim(),
          addressPostalCode: (d.addressPostalCode ?? '').trim(),
          addressCountry: (d.addressCountry ?? '').trim(),
        };
        if (Object.values(draftObj).some(Boolean)) {
          normalized.newContactDraft = JSON.stringify(draftObj);
        }
      }

      return normalized;
    },
    [],
  );

  /**
   * Normalizes signature fields for comparison.
   */
  const normalizeSignatureFields = useCallback(
    (
      data: CompleteProviderFormData,
    ): {
      providerSignatureName: string;
      providerSignatureRole: string;
      signatureTermsAgreed: boolean;
    } => {
      return {
        providerSignatureName: data.providerSignatureName || '',
        providerSignatureRole: data.providerSignatureRole || '',
        signatureTermsAgreed: coerceCheckboxBoolean(data.signatureTermsAgreed),
      };
    },
    [],
  );

  /**
   * Normalizes answers array for comparison.
   */
  const normalizeAnswers = useCallback(
    (
      answers: CompleteProviderFormData['answers'],
    ): Array<{
      value: string | null;
      values: string[];
      contactId: string | null;
      newContactDraft: string | null;
    }> => {
      if (!answers) return [];
      return answers.map((a) => normalizeAnswer(a));
    },
    [normalizeAnswer],
  );

  /**
   * Normalizes forms object for multi-form mode comparison.
   */
  const normalizeForms = useCallback(
    (
      forms: CompleteProviderFormData['forms'],
    ): Record<
      string,
      {
        providerSignatureName: string;
        providerSignatureRole: string;
        signatureTermsAgreed: boolean;
        answers: Array<{
          value: string | null;
          values: string[];
          contactId: string | null;
          newContactDraft: string | null;
        }>;
      }
    > => {
      if (!forms) return {};

      const normalized: Record<
        string,
        {
          providerSignatureName: string;
          providerSignatureRole: string;
          signatureTermsAgreed: boolean;
          answers: Array<{
            value: string | null;
            values: string[];
            contactId: string | null;
            newContactDraft: string | null;
          }>;
        }
      > = {};

      Object.keys(forms)
        .sort()
        .forEach((key) => {
          const form = forms[key];
          normalized[key] = {
            providerSignatureName: form.providerSignatureName || '',
            providerSignatureRole: form.providerSignatureRole || '',
            signatureTermsAgreed: coerceCheckboxBoolean(
              form.signatureTermsAgreed,
            ),
            answers: normalizeAnswers(form.answers),
          };
        });

      return normalized;
    },
    [normalizeAnswers],
  );

  /**
   * Normalizes form data for comparison (creates a serializable representation).
   * This function handles both single form mode and multi-form mode.
   */
  const normalizeFormData = useCallback(
    (data: CompleteProviderFormData): string => {
      const normalized: any = {};

      if (data.forms) {
        // Multi-form mode
        normalized.forms = normalizeForms(data.forms);
      } else {
        // Single form mode
        if (data.answers) {
          normalized.answers = normalizeAnswers(data.answers);
        }
        Object.assign(normalized, normalizeSignatureFields(data));
      }

      return JSON.stringify(normalized);
    },
    [normalizeAnswers, normalizeForms, normalizeSignatureFields],
  );

  // Helper function to check if form data has actually changed
  const hasFormDataChanged = useCallback(
    (
      current: CompleteProviderFormData,
      previousNormalized: string | null,
    ): boolean => {
      if (!previousNormalized) return true; // First time, consider it changed

      // Use normalized comparison for more reliable change detection
      const currentNormalized = normalizeFormData(current);

      return currentNormalized !== previousNormalized;
    },
    [normalizeFormData],
  );

  /**
   * Saves a single form draft
   */
  const saveSingleFormDraft = useCallback(
    async (
      assessmentId: string,
      formQuestions: FormQuestion[],
      formData: CompleteProviderFormData,
    ): Promise<void> => {
      // Convert form values to draft input format
      const draftAnswers = formValuesToDraftInput(
        formData,
        formQuestions,
        assessmentId,
      );

      const formAnswers =
        formData.forms?.[assessmentId]?.answers ?? formData.answers;

      // Filter out empty answers
      const nonEmptyDraftAnswers = draftAnswers.filter((answer, idx) => {
        const hasAnswer =
          answer.answer !== undefined &&
          answer.answer !== null &&
          answer.answer.trim() !== '';
        const hasAnswers =
          answer.answers !== undefined &&
          answer.answers !== null &&
          answer.answers.length > 0;
        const hasSignature =
          answer.signatureName !== undefined &&
          answer.signatureName !== null &&
          answer.signatureName.trim() !== '';
        const hasContactId =
          answer.contactId !== undefined &&
          answer.contactId !== null &&
          String(answer.contactId).trim() !== '';
        const q = formQuestions[idx];
        const row = formAnswers?.[idx];
        const draft = row?.newContactDraft;
        const hasPatientContactInProgress =
          q &&
          isPatientContactAssessmentQuestion(q) &&
          (String(row?.contactId ?? '').trim() ===
            PATIENT_CONTACT_NEW_SENTINEL ||
            Boolean(
              draft && Object.values(draft).some((v) => String(v ?? '').trim()),
            ));
        return (
          hasAnswer ||
          hasAnswers ||
          hasSignature ||
          hasContactId ||
          hasPatientContactInProgress
        );
      });

      // Extract signature fields (for single form mode or per-form in multi-form mode)
      const formDataForThisForm = formData.forms?.[assessmentId];
      const signatureFields = formDataForThisForm
        ? {
            providerSignatureName:
              formDataForThisForm.providerSignatureName?.trim() || null,
            providerSignatureRole:
              formDataForThisForm.providerSignatureRole?.trim() || null,
            signatureTermsAgreed: coerceCheckboxBoolean(
              formDataForThisForm.signatureTermsAgreed,
            ),
          }
        : !formData.forms
        ? {
            providerSignatureName:
              formData.providerSignatureName?.trim() || null,
            providerSignatureRole:
              formData.providerSignatureRole?.trim() || null,
            signatureTermsAgreed: coerceCheckboxBoolean(
              formData.signatureTermsAgreed,
            ),
          }
        : null;

      const hasSignatureFieldsToSave =
        signatureFields &&
        (signatureFields.providerSignatureName ||
          signatureFields.providerSignatureRole ||
          signatureFields.signatureTermsAgreed);

      // Skip if no data to save
      if (nonEmptyDraftAnswers.length === 0 && !hasSignatureFieldsToSave) {
        return;
      }

      const saveInput: SaveFormDraftInput = {
        programAssessmentId: assessmentId,
        draftAnswers: nonEmptyDraftAnswers,
        ...(signatureFields && {
          providerSignatureName:
            signatureFields.providerSignatureName || undefined,
          providerSignatureRole:
            signatureFields.providerSignatureRole || undefined,
          signatureTermsAgreed: signatureFields.signatureTermsAgreed
            ? true
            : undefined,
        }),
      };

      const saveResult = await saveFormDraft({
        variables: {
          input: saveInput,
        },
      });

      if (saveResult.errors && saveResult.errors.length > 0) {
        throw new Error(
          `Failed to save draft for ${assessmentId}: ${saveResult.errors
            .map((e) => e.message)
            .join(', ')}`,
        );
      }

      if (!saveResult.data?.saveFormDraft?.success) {
        throw new Error(
          `Failed to save draft for ${assessmentId} - backend returned success: false`,
        );
      }
    },
    [saveFormDraft],
  );

  const saveDraft = useCallback(
    async (skipRefetch = false) => {
      if (!enabled) return;

      // In multi-form mode, we need programAssessmentIds
      if (
        isMultiFormMode &&
        (!programAssessmentIds || programAssessmentIds.length === 0)
      ) {
        return;
      }

      // In single form mode, we need programAssessmentId
      if (!isMultiFormMode && !programAssessmentId) {
        return;
      }

      try {
        setIsSaving(true);
        setSaveError(null);

        const formData = latestFormData.current;

        // Skip saving if there's no data and this is the first render
        if (isFirstRender.current) {
          const hasData = isMultiFormMode
            ? formData.forms &&
              Object.values(formData.forms).some(
                (form) =>
                  form.answers.some(
                    (a) =>
                      a.value ||
                      (a.values && a.values.length > 0) ||
                      (a.contactId && String(a.contactId).trim() !== '') ||
                      Boolean(
                        a.newContactDraft &&
                          Object.values(a.newContactDraft).some((v) =>
                            String(v ?? '').trim(),
                          ),
                      ),
                  ) ||
                  form.providerSignatureName ||
                  form.providerSignatureRole ||
                  form.signatureTermsAgreed,
              )
            : formData.answers?.some(
                (a) =>
                  a.value ||
                  (a.values && a.values.length > 0) ||
                  (a.contactId && String(a.contactId).trim() !== '') ||
                  Boolean(
                    a.newContactDraft &&
                      Object.values(a.newContactDraft).some((v) =>
                        String(v ?? '').trim(),
                      ),
                  ),
              ) ||
              formData.providerSignatureName ||
              formData.providerSignatureRole ||
              formData.signatureTermsAgreed;

          if (!hasData) {
            setIsSaving(false);
            return;
          }
        }

        // Handle multi-form mode: save all forms
        if (isMultiFormMode && programAssessmentIds && questionsMap) {
          const savePromises = programAssessmentIds.map(
            async (assessmentId) => {
              const formQuestions = questionsMap.get(assessmentId);
              if (!formQuestions) {
                return {
                  assessmentId,
                  success: false,
                  error: 'No questions found',
                };
              }

              try {
                await saveSingleFormDraft(
                  assessmentId,
                  formQuestions,
                  formData,
                );
                return { assessmentId, success: true };
              } catch (error) {
                logger.error(
                  error instanceof Error
                    ? error
                    : new Error(`Error saving draft for ${assessmentId}`),
                  { assessmentId, error },
                );
                return {
                  assessmentId,
                  success: false,
                  error:
                    error instanceof Error ? error : new Error('Unknown error'),
                };
              }
            },
          );

          const results = await Promise.all(savePromises);
          const failures = results.filter((r) => !r.success);

          if (failures.length > 0) {
            // Some forms failed, but don't fail the entire operation
            // Log the failures and set a partial error state
            const failedIds = failures.map((f) => f.assessmentId).join(', ');
            const errorMessage = `Failed to save drafts for some forms: ${failedIds}`;
            logger.warn(errorMessage, { failures });

            // Only set error if ALL forms failed
            if (failures.length === results.length) {
              setSaveError(new Error(errorMessage));
              setIsSaving(false);
              return;
            }
            // If some succeeded, continue but log the partial failure
          }

          // Skip refetching in multi-form mode during auto-save to avoid page refresh/re-render
          // The drafts are saved on the backend, and will be loaded when the user navigates away and back
          // We only refetch on manual save (via manualSave function) or if explicitly requested
          if (!skipRefetch && refetchQuery) {
            await new Promise((resolve) =>
              setTimeout(resolve, FORM_DRAFT_REFETCH_DELAY_MS),
            );
            try {
              await refetchQuery(undefined, { fetchPolicy: 'network-only' });
            } catch (refetchError) {
              logger.error(
                refetchError instanceof Error
                  ? refetchError
                  : new Error('Error refetching query after draft save'),
                { error: refetchError },
              );
            }
          }
        } else {
          // Single form mode
          const formQuestions = questions || [];
          if (formQuestions.length === 0) {
            setIsSaving(false);
            return;
          }

          await saveSingleFormDraft(
            programAssessmentId,
            formQuestions,
            formData,
          );

          // Refetch the query to update the cache
          if (refetchQuery) {
            try {
              await new Promise((resolve) =>
                setTimeout(resolve, FORM_DRAFT_REFETCH_DELAY_MS),
              );

              const result = await refetchQuery(undefined, {
                fetchPolicy: 'network-only',
              });

              if (
                !result.data?.programAssessment?.draftAnswers ||
                result.data.programAssessment.draftAnswers.length === 0
              ) {
                logger.warn(
                  'Backend returned null or empty draftAnswers after save. This indicates a backend issue - drafts are not being persisted or returned correctly.',
                  {
                    programAssessmentId,
                  },
                );
              }
            } catch (refetchError) {
              logger.error(
                refetchError instanceof Error
                  ? refetchError
                  : new Error('Error refetching query after draft save'),
                {
                  programAssessmentId,
                  error: refetchError,
                },
              );
            }
          }
        }

        setLastSaved(new Date());
        setIsSaving(false);
      } catch (err) {
        logger.error(
          err instanceof Error ? err : new Error('Error saving draft'),
          {
            programAssessmentId,
            error: err,
          },
        );
        setSaveError(err as Error);
        setIsSaving(false);
      }
    },
    [
      enabled,
      isMultiFormMode,
      programAssessmentId,
      programAssessmentIds,
      questions,
      questionsMap,
      saveSingleFormDraft,
      refetchQuery,
    ],
  );

  // Track whether to skip refetch for the next auto-save
  const skipRefetchRef = useRef(false);

  // Create debounced save function using useRef to prevent recreation
  const debouncedSaveRef = useRef(
    debounce(() => {
      saveDraft(skipRefetchRef.current);
      skipRefetchRef.current = false; // Reset after save
    }, debounceMs),
  );

  // Update debounce delay if it changes
  useEffect(() => {
    debouncedSaveRef.current.cancel();
    debouncedSaveRef.current = debounce(() => {
      saveDraft(skipRefetchRef.current);
      skipRefetchRef.current = false;
    }, debounceMs);
  }, [debounceMs, saveDraft]);

  const debouncedSave = debouncedSaveRef.current;

  // Reset first render flag and last saved when programAssessmentId changes
  useEffect(() => {
    if (previousProgramAssessmentId.current !== programAssessmentId) {
      isFirstRender.current = true;
      setLastSaved(null);
      setSaveError(null);
      previousNormalizedFormData.current = null;
      debouncedSave.cancel();
      previousProgramAssessmentId.current = programAssessmentId;
    }
  }, [programAssessmentId, debouncedSave]);

  // Trigger save when formData changes (only if there are actual changes)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      previousNormalizedFormData.current = normalizeFormData(formData);
      return;
    }

    // Check if form data has actually changed using normalized comparison
    const currentNormalized = normalizeFormData(formData);
    const hasChanged = hasFormDataChanged(
      formData,
      previousNormalizedFormData.current,
    );

    if (enabled && hasChanged) {
      previousNormalizedFormData.current = currentNormalized;
      // Skip refetch during auto-save in multi-form mode to avoid page refresh
      skipRefetchRef.current = isMultiFormMode;
      debouncedSave();
    } else {
      previousNormalizedFormData.current = currentNormalized;
    }

    return () => {
      debouncedSave.cancel();
    };
  }, [
    formData,
    debouncedSave,
    enabled,
    hasFormDataChanged,
    normalizeFormData,
    isMultiFormMode,
  ]);

  // Manual save function (bypasses debounce and always refetches)
  const manualSave = useCallback(async () => {
    debouncedSave.cancel();
    await saveDraft(false); // Always refetch on manual save
  }, [saveDraft, debouncedSave]);

  return {
    isSaving,
    lastSaved,
    saveError,
    manualSave,
  };
};
